import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback, Page, ConfirmDialog } from "components/index";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Stack, TextField, Autocomplete, Toolbar, Typography, ToggleButton, ToggleButtonGroup, Card, CardContent } from "@mui/material";
import TreeView from "@mui/lab/TreeView";
import TreeItem from "@mui/lab/TreeItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import ReactFlow, { MiniMap, Controls, Background, useNodesState, useEdgesState, MarkerType } from "reactflow";
import "reactflow/dist/style.css";
import SuittService from "src/services/SuittService";
import { snackActions } from "src/utils/SnackBarUtils";
import { StoreContext } from "src/App";

const defaultNodeStyle = {
  background: "#fff",
  border: "2px solid #222",
  borderRadius: "8px",
  padding: "10px",
  minWidth: "140px",
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
};

const selectedNodeStyle = {
  ...defaultNodeStyle,
  background: "#e3f2fd",
  border: "2px solid #1976d2",
  boxShadow: "0 0 10px rgba(25, 118, 210, 0.3)",
};

const OrgStructure = () => {
  const { auth } = useContext(StoreContext);
  const service = useMemo(() => new SuittService(), []);
  const [loading, setLoading] = useState(false);
  const [tree, setTree] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [flat, setFlat] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [viewMode, setViewMode] = useState("tree");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [form, setForm] = useState({ id: "", code: "", nameUk: "", parentCode: null });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const buildFlat = useCallback((nodes) => {
    const acc = [];
    const walk = (n, depth, parent) => {
      const item = { ...n, _depth: depth, _parent: parent || null };
      acc.push(item);
      if (Array.isArray(n.children)) {
        n.children.forEach((c) => walk(c, depth + 1, n));
      }
    };
    nodes.forEach((n) => walk(n, 0, null));
    return acc;
  }, []);

  const buildLayoutedElements = useCallback((tree) => {
    const nodesList = [];
    const edgesList = [];
    const visited = new Set();
    const xGap = 180;
    const yGap = 160;

    const calculateTreeWidth = (node) => {
      if (!Array.isArray(node.children) || node.children.length === 0) {
        return 1;
      }
      const childrenWidth = node.children.reduce((sum, child) => sum + calculateTreeWidth(child), 0);
      return Math.max(1, childrenWidth);
    };

    const walk = (node, depth, offset) => {
      if (visited.has(node._id)) return;
      visited.add(node._id);

      const yPos = depth * yGap;
      const xPos = offset * xGap;

      nodesList.push({
        id: String(node._id),
        data: {
          label: (
            <Box sx={{ cursor: "pointer" }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {node.nameUk}
              </Typography>
              <Box sx={{ mt: 0.5, display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
                <Chip size="small" label={node.code} sx={{ height: "auto", fontSize: "0.65rem" }} />
              </Box>
            </Box>
          ),
        },
        position: { x: xPos, y: yPos },
        style: selectedId === String(node._id) ? selectedNodeStyle : defaultNodeStyle,
      });

      if (Array.isArray(node.children) && node.children.length > 0) {
        let childOffset = offset - calculateTreeWidth(node) / 2 + 0.5;
        node.children.forEach((child) => {
          edgesList.push({
            id: `e${node._id}-${child._id}`,
            source: String(node._id),
            target: String(child._id),
            markerEnd: { type: MarkerType.ArrowClosed },
            animated: false,
          });
          const childWidth = calculateTreeWidth(child);
          walk(child, depth + 1, childOffset + childWidth / 2 - 0.5);
          childOffset += childWidth;
        });
      }
    };

    tree.forEach((node) => walk(node, 0, 0));
    return { nodesList, edgesList };
  }, [selectedId]);

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await service.getOrgUnitsTree();
      if (res?.status && Array.isArray(res.data)) {
        setTree(res.data);
        const f = buildFlat(res.data);
        setFlat(f);
        const toExpand = new Set();
        f.forEach((n) => {
          if (n._depth <= 1) toExpand.add(String(n._id));
        });
        setExpanded(Array.from(toExpand));

        const { nodesList, edgesList } = buildLayoutedElements(res.data);
        setNodes(nodesList);
        setEdges(edgesList);

        if (res.data.length > 0 && isFirstLoad) {
          const rootNode = res.data[0];
          setSelectedId(String(rootNode._id));
          setIsFirstLoad(false);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [service, buildFlat, buildLayoutedElements, setNodes, setEdges, isFirstLoad]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const getPathToRoot = useCallback((nodeId) => {
    const pathIds = new Set([nodeId]);
    let currentNode = flat.find((n) => String(n._id) === String(nodeId));
    
    while (currentNode?.parentCode) {
      const parent = flat.find((n) => n.code === currentNode.parentCode);
      if (parent) {
        pathIds.add(String(parent._id));
        currentNode = parent;
      } else {
        break;
      }
    }
    
    return pathIds;
  }, [flat]);

  const handleFlowInit = useCallback((instance) => {
    setTimeout(() => {
      instance.fitView({ padding: 0.2 });
    }, 100);
  }, []);

  useEffect(() => {
    if (hoveredNodeId && viewMode === "flow") {
      const pathToRoot = getPathToRoot(hoveredNodeId);
      
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          style: selectedId === node.id
            ? selectedNodeStyle
            : pathToRoot.has(node.id)
            ? {
                ...selectedNodeStyle,
                boxShadow: "0 0 15px rgba(25, 118, 210, 0.6)",
              }
            : {
                ...defaultNodeStyle,
                opacity: 0.4,
              },
        }))
      );

      setEdges((eds) =>
        eds.map((edge) => ({
          ...edge,
          style: pathToRoot.has(edge.source) && pathToRoot.has(edge.target)
            ? { stroke: "#1976d2", strokeWidth: 3 }
            : { stroke: "#ccc", strokeWidth: 1, opacity: 0.2 },
          animated: pathToRoot.has(edge.source) && pathToRoot.has(edge.target),
        }))
      );
    } else if (viewMode === "flow") {
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          style: selectedId === node.id ? selectedNodeStyle : defaultNodeStyle,
        }))
      );

      setEdges((eds) =>
        eds.map((edge) => ({
          ...edge,
          style: { stroke: "#333", strokeWidth: 1 },
          animated: false,
        }))
      );
    }
  }, [hoveredNodeId, viewMode, selectedId, getPathToRoot, setNodes, setEdges]);

  const selected = useMemo(() => flat.find((f) => String(f._id) === String(selectedId)) || null, [flat, selectedId]);

  const openCreate = (parent) => {
    setDialogMode("create");
    setForm({ id: "", code: "", nameUk: "", parentCode: parent?.code || null });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    if (!item) return;
    setDialogMode("edit");
    setForm({ id: item._id, code: item.code, nameUk: item.nameUk, parentCode: item.parentCode || null });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.code || !form.nameUk) {
      snackActions.warning("Заповніть усі обов'язкові поля");
      return;
    }
    if (dialogMode === "create") {
      const res = await service.createOrgUnit({ code: form.code.trim(), nameUk: form.nameUk.trim(), type: "unit", parentCode: form.parentCode || null });
      if (res?.status) {
        snackActions.success("Створено");
        setDialogOpen(false);
        await loadTree();
      } else {
        snackActions.error("Помилка створення");
      }
    } else {
      const res = await service.updateOrgUnit(form.id, { code: form.code.trim(), nameUk: form.nameUk.trim(), parentCode: form.parentCode || null });
      if (res?.status) {
        snackActions.success("Збережено");
        setDialogOpen(false);
        await loadTree();
      } else {
        snackActions.error("Помилка збереження");
      }
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    const res = await service.deleteOrgUnit(selected._id);
    if (res?.status) {
      snackActions.success("Видалено");
      setDeleteOpen(false);
      setSelectedId("");
      await loadTree();
    } else {
      snackActions.error("Неможливо видалити. Перевірте наявність дочірніх або прив'язаних користувачів");
    }
  };

  const getDescendantCodes = useCallback((node) => {
    const set = new Set();
    const dfs = (n) => {
      set.add(n.code);
      if (Array.isArray(n.children)) n.children.forEach(dfs);
    };
    if (node) dfs(node);
    return set;
  }, []);

  const ParentSelect = ({ value, onChange, excludeCodes = new Set() }) => {
    const options = useMemo(() => [{ code: null, _indentLabel: "Корінь" }, ...flat]
      .filter((i) => !excludeCodes.has(i.code))
      .map((i) => ({ code: i.code, label: i._indentLabel ? i._indentLabel : i.nameUk })), [flat, excludeCodes]);
    const current = options.find((o) => o.code === value) || null;
    return (
      <Autocomplete
        options={options}
        value={current}
        onChange={(e, v) => onChange(v ? v.code : null)}
        isOptionEqualToValue={(o, v) => o.code === v.code}
        renderInput={(params) => <TextField {...params} label="Батьківський підрозділ" size="small" />}
        disableClearable={false}
      />
    );
  };

  const renderLabel = (node) => (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2">{node.nameUk}</Typography>
      <Chip size="small" variant="outlined" label={node.code} />
    </Stack>
  );

  const renderTree = (nodes) => {
    return nodes.map((n) => (
      <TreeItem key={n._id} nodeId={String(n._id)} label={renderLabel(n)}>
        {Array.isArray(n.children) && n.children.length > 0 && renderTree(n.children)}
      </TreeItem>
    ));
  };

  const canEdit = auth?.user?.accountType === "HR" && auth?.user?.status === "active";

  const onNodeClick = (event, node) => {
    setSelectedId(node.id);
    setHoveredNodeId(null);
  };

  const onNodeMouseEnter = (event, node) => {
    setHoveredNodeId(node.id);
  };

  const onNodeMouseLeave = () => {
    setHoveredNodeId(null);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Page
        title={"Організаційна структура"}
        actions={(
          <Toolbar disableGutters sx={{ gap: 1 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="tree" aria-label="tree view">
                <AccountTreeIcon sx={{ mr: 0.5 }} /> Дерево
              </ToggleButton>
              <ToggleButton value="flow" aria-label="flow view">
                <CallSplitIcon sx={{ mr: 0.5 }} /> Граф
              </ToggleButton>
            </ToggleButtonGroup>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Button startIcon={<AddIcon />} variant="contained" onClick={() => openCreate(null)} disabled={!canEdit}>Додати кореневий</Button>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => openCreate(selected)} disabled={!canEdit || !selected}>Додати дочірній</Button>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Button startIcon={<EditIcon />} onClick={() => openEdit(selected)} disabled={!canEdit || !selected}>Редагувати</Button>
            <Button startIcon={<DeleteIcon />} color="error" onClick={() => setDeleteOpen(true)} disabled={!canEdit || !selected}>Видалити</Button>
          </Toolbar>
        )}
      >

        {viewMode === "tree" ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1, minHeight: 400 }}>
                <TreeView
                  defaultExpandIcon={<ChevronRightIcon />}
                  defaultCollapseIcon={<ExpandMoreIcon />}
                  expanded={expanded}
                  onNodeToggle={(e, ids) => setExpanded(ids)}
                  onNodeSelect={(e, nodeId) => setSelectedId(nodeId)}
                >
                  {renderTree(tree)}
                </TreeView>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
                {selected ? (
                  <Card elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Вибрано</Typography>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">Код</Typography>
                          <Typography variant="body2">{selected.code}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="textSecondary">Назва</Typography>
                          <Typography variant="body2">{selected.nameUk}</Typography>
                        </Box>
                        {selected.parentCode && (
                          <Box>
                            <Typography variant="caption" color="textSecondary">Батьківський</Typography>
                            <Typography variant="body2">{selected.parentCode}</Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography variant="caption" color="textSecondary">Рівень в дереві</Typography>
                          <Typography variant="body2">{selected._depth}</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : (
                  <Typography variant="body2" color="text.secondary">Оберіть елемент дерева</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: "flex", gap: 2, height: "calc(100vh - 250px)" }}>
            <Box sx={{ flex: 1, border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={onNodeClick}
                onNodeMouseEnter={onNodeMouseEnter}
                onNodeMouseLeave={onNodeMouseLeave}
                fitView
                onInit={handleFlowInit}
              >
                <Background />
                <Controls />
                <MiniMap />
              </ReactFlow>
            </Box>

            <Box sx={{ width: 320, border: 1, borderColor: "divider", borderRadius: 1, p: 2, overflow: "auto" }}>
              {selected ? (
                <Card elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Вибраний елемент</Typography>
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Код</Typography>
                        <Typography variant="body2">{selected.code}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Назва</Typography>
                        <Typography variant="body2">{selected.nameUk}</Typography>
                      </Box>
                      {selected.parentCode && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">Батьківський</Typography>
                          <Typography variant="body2">{selected.parentCode}</Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="caption" color="textSecondary">Рівень в дереві</Typography>
                        <Typography variant="body2">{selected._depth}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ) : (
                <Typography variant="body2" color="textSecondary">Клікніть на елемент</Typography>
              )}
            </Box>
          </Box>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{dialogMode === "create" ? "Новий підрозділ" : "Редагувати підрозділ"}</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Stack spacing={2}>
              <TextField label="Код" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} size="small" autoFocus />
              <TextField label="Назва (укр.)" value={form.nameUk} onChange={(e) => setForm({ ...form, nameUk: e.target.value })} size="small" />
              <ParentSelect
                value={form.parentCode}
                onChange={(v) => setForm({ ...form, parentCode: v })}
                excludeCodes={dialogMode === "edit" ? getDescendantCodes(flat.find((f) => f._id === form.id)) : new Set()}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="inherit">Відмінити</Button>
            <Button onClick={handleSubmit} variant="contained">Зберегти</Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onConfirm={handleDelete}
          title={"Підтвердити видалення"}
          content={"Цю дію не можна скасувати"}
          confirmText={"Видалити"}
        />
      </Page>
    </ErrorBoundary>
  );
};

export default OrgStructure;


